import { Button, Card, List, Skeleton, Stack, Text, Title } from '@mantine/core';
import { IconCreditCard } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { usePlanPrice } from '../lib/hooks/usePlanPrice';

/** `4999` -> `'$49.99'`. Matches the formatting used on `/subscribe`. */
function formatMonthlyPrice(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}

/**
 * Renders the "subscribe to Vendor" upsell shown when an account's gate is
 * lapsed (`writesAllowed === false`).
 *
 * Shell: (desktop). Used by {@link SubscriptionGate} as the fallback when the
 * caller's account is unsubscribed; not rendered directly. The Subscribe CTA
 *
 * ## vendor#808 — this list must name only features that exist
 *
 * It once advertised **slab binders**, **multi-location stock/transfers** and
 * **audit sessions** — all storefront-fork residue, all explicitly dropped, and
 * all being sold to a lapsed dealer ON THE PAYWALL as what upgrading buys them.
 * That list is corrected. The word "shop" went with it: this repo has accounts,
 * never shops, and "No shop selected" / "Shop not found" were user-facing.
 *
 * Before adding a line here, check it against `CLAUDE.md`'s "What was dropped
 * vs. Storefront". A paywall is the worst place in the product to name a
 * feature that does not exist.
 * routes to `/subscribe` (the in-app checkout gate — see `router.tsx`) via
 * React Router's `Link`, matching the rest of this codebase's in-app
 * navigation idiom rather than a raw anchor.
 *
 * Non-obvious contracts:
 *   - The status string is only displayed for staff diagnostics — copy
 *     deliberately stays neutral so owners aren't blamed for an account's
 *     `past_due` state that they're already getting Stripe emails about.
 *   - The price is read from {@link usePlanPrice} (`public.tiers.
 *     monthly_price_cents`), never a literal — vendor#156 existed because
 *     this card, `/subscribe`, the `tiers` row and the now-deleted onboarding
 *     wizard each hardcoded a different number. While the price is loading, a
 *     skeleton stands in for it; if the query fails, an error line replaces
 *     it and the CTA is disabled — this card must never show a wrong price or
 *     invite a click toward one it can't confirm.
 */
export interface UpsellCardProps {
  /** The account's tier id — surfaced in the help text. */
  tierId?: string;
  /**
   * The account's current subscription_status — surfaced in the help text so
   * staff can debug "wait, why am I seeing the upsell?" without opening psql.
   */
  status?: string;
}

export function UpsellCard({ tierId, status }: UpsellCardProps) {
  const priceQuery = usePlanPrice();
  const priceLabel = priceQuery.data
    ? `${formatMonthlyPrice(priceQuery.data.monthly_price_cents)}/mo`
    : null;

  return (
    <Card withBorder padding="xl" radius="md" maw={620} mx="auto" mt="xl">
      <Stack gap="md">
        <Title order={2}>Subscribe to MyTCGBinder Vendor</Title>

        {priceQuery.isLoading && (
          <Skeleton height={16} width={100} radius="sm" data-testid="upsell-price-loading" />
        )}
        {priceQuery.isError && (
          <Text size="sm" c="red" data-testid="upsell-price-error">
            Couldn&apos;t load the current price — refresh to try again.
          </Text>
        )}
        {priceLabel && (
          <Text fw={600} data-testid="upsell-price">
            {priceLabel}
          </Text>
        )}

        <Text c="dimmed">
          MyTCGBinder Vendor unlocks inventory, label printing, kiosk browse, and the rest of the
          vendor tools. Your data is preserved while you decide — re-subscribe at any time and pick
          up where you left off.
        </Text>

        <List size="sm" spacing={4}>
          <List.Item>Bins, binders, display cases, sealed stock, vault</List.Item>
          <List.Item>Avery / Dymo / Brother / round-1″ label printing</List.Item>
          <List.Item>Card analytics, buys/sales/trades, kiosk browse</List.Item>
        </List>

        <Button
          component={Link}
          to="/subscribe"
          leftSection={<IconCreditCard size={18} stroke={1.5} />}
          size="md"
          disabled={!priceLabel}
          data-testid="upsell-subscribe-cta"
        >
          {priceLabel
            ? `Subscribe to MyTCGBinder Vendor — ${priceLabel}`
            : 'Subscribe to MyTCGBinder Vendor'}
        </Button>

        {(tierId !== undefined || status !== undefined) && (
          <Text size="xs" c="dimmed">
            Current state: tier <code>{tierId ?? 'unknown'}</code>, status{' '}
            <code>{status ?? 'unknown'}</code>. Contact{' '}
            <a href="mailto:support@mytcgbinder.com">support@mytcgbinder.com</a> if this looks
            wrong.
          </Text>
        )}
      </Stack>
    </Card>
  );
}
